import { useEffect, useRef } from "react";

import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { NewTabLink } from "components/NewTabLink";
import {
  getQuoteScoreLevelStyle,
  QuoteScoreBreakdown,
} from "components/QuoteScore";
import {
  ClientAppEvent,
  QUOTE_COMPETITIVENESS_SCORE_BLOG_URL,
} from "constants/index";
import { useDisclosure, useQuoteCompetitivenessScore } from "hooks";
import {
  getQuoteScoreStatusIcon,
  isDeadQuote,
  isWithinLastMinutes,
} from "utils";

import { QuoteScoreLocked } from "./QuoteScoreLocked";
import { QUOTE_SCORE_STATUS_COPY, QuoteScoreStrip } from "./QuoteScoreStrip";

// Past this, a still-pending quote's next score is on an unknown timeline, not imminent.
const PENDING_RECENCY_THRESHOLD_MINUTES = 5;

interface Props {
  quoteId: number;
  internalState: InternalQuoteStatusValue;
  // The latest QuoteAction's timestamp — CondensedQuote.created_at, despite the name.
  lastActionAt: string;
}

/**
 * The carrier's own view of their quote's competitiveness score
 * (docs/engineering/design/quote_competitiveness_scoring_design.md §14) — dropped into
 * CarrierQuoteDetail directly under the price/status row.
 */
export function QuoteScorePanel({
  quoteId,
  internalState,
  lastActionAt,
}: Props) {
  const posthog = usePostHog();
  const { isOpen, onOpen, onClose } = useDisclosure(false);
  const { score, isLoading, error } = useQuoteCompetitivenessScore(quoteId);
  const statusIcon = getQuoteScoreStatusIcon(internalState);
  // Won or dead quotes get no further scoring trigger without a backfill (not in this MVP).
  const canReceiveFutureScore = statusIcon === "circle";
  const isPendingScoreImminent = isWithinLastMinutes(
    lastActionAt,
    PENDING_RECENCY_THRESHOLD_MINUTES,
  );
  const hasCapturedView = useRef(false);

  useEffect(() => {
    if (
      hasCapturedView.current ||
      isLoading ||
      error != null ||
      score == null
    ) {
      return;
    }
    // A pending score that isn't imminent (or can never arrive) renders
    // nothing (see below) — there's no panel to have "viewed" in that case.
    if (
      "pending" in score &&
      (!canReceiveFutureScore || !isPendingScoreImminent)
    ) {
      return;
    }
    hasCapturedView.current = true;
    let scoreState: "gated" | "pending" | "scored";
    if ("gated" in score) {
      scoreState = "gated";
    } else if ("pending" in score) {
      scoreState = "pending";
    } else {
      scoreState = "scored";
    }
    posthog?.capture(
      ClientAppEvent.CARRIER_QUOTE_COMPETITIVENESS_SCORE_VIEWED,
      {
        quote_id: quoteId,
        score_state: scoreState,
      },
    );
  }, [
    isLoading,
    error,
    score,
    canReceiveFutureScore,
    isPendingScoreImminent,
    posthog,
    quoteId,
  ]);

  const onToggle = () => {
    if (isOpen) {
      onClose();
    } else {
      posthog?.capture(
        ClientAppEvent.CARRIER_QUOTE_COMPETITIVENESS_SCORE_PANEL_EXPANDED,
      );
      onOpen();
    }
  };

  const onLearnMoreClick = () => {
    posthog?.capture(
      ClientAppEvent.CARRIER_QUOTE_COMPETITIVENESS_SCORE_LEARN_MORE_CLICKED,
    );
  };

  if (isLoading) {
    return <Skeleton variant="rounded" width="100%" height={48} />;
  }

  if (error != null) {
    return <ErrorTypography error={error} />;
  }

  if (score == null) {
    return null;
  }

  if ("gated" in score) {
    return <QuoteScoreLocked />;
  }

  if ("pending" in score) {
    if (!canReceiveFutureScore || !isPendingScoreImminent) {
      return null;
    }
    return (
      <Box
        sx={{
          width: "100%",
          pl: "12px",
          pr: 1.5,
          py: 1.25,
          border: "1px solid",
          borderColor: "divider",
          borderLeft: "4px solid",
          borderLeftColor: "grey.300",
          borderRadius: "4px",
          backgroundColor: "grey.50",
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
          Scoring your quote…
        </Typography>
      </Box>
    );
  }

  const muted = isDeadQuote(internalState);
  const ceilingCopy =
    score.ceiling_label != null
      ? `Price capped this to ${getQuoteScoreLevelStyle(score.ceiling_label).label} — see Price below for why.`
      : null;

  return (
    <Box sx={{ width: "100%" }}>
      <QuoteScoreStrip
        level={score.overall_label}
        muted={muted}
        statusIcon={statusIcon}
        isOpen={isOpen}
        onToggle={onToggle}
      />
      {isOpen && (
        <Box sx={{ pt: 1.5, pb: 0.5, px: "12px" }}>
          {statusIcon && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  lineHeight: 1.5,
                  color: "text.secondary",
                }}
              >
                {QUOTE_SCORE_STATUS_COPY[statusIcon]}
              </Typography>
              <NewTabLink
                href={QUOTE_COMPETITIVENESS_SCORE_BLOG_URL}
                onClick={onLearnMoreClick}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  Learn more
                </Typography>
              </NewTabLink>
            </Stack>
          )}
          {ceilingCopy != null && (
            <Typography
              sx={{
                fontSize: "13px",
                lineHeight: 1.5,
                color: "text.secondary",
                mb: 1.5,
              }}
            >
              {ceilingCopy}
            </Typography>
          )}
          <QuoteScoreBreakdown result={score} muted={muted} />
        </Box>
      )}
    </Box>
  );
}
